import { Button, Col, Form, Row, Space, theme } from "antd";
import { useEffect, useState } from "react";
import Data from "../../controls/Data";
import { SearchQuery } from "../../code/search-query/search-query";
import {
  questionnairesViewColumns,
  mapQuestionnairesViewQuery,
  QUESTIONNAIRE_STATUSES,
} from "./QuestionnairesView.default";
import { getQuestionnaires } from "../../repository/data.repository";
import DynamicForm from "../../controls/DynamicForm";
import { createGridRequest } from "../../hooks/useGridRequest";
import { useSelector } from "../../store/store";
import { authorizeSelectors } from "../../store/authorize";
import { DataAction } from "../../controls/Data.default";
import { Questionnaire } from "../../repository/data.default";

export interface QuestionnairesViewProps {
  readonly trigger?: string;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
}

function QuestionnairesView(props: QuestionnairesViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const permissions = useSelector(authorizeSelectors.permissions);

  // What a version offers follows its status: a Draft is edited, published or discarded; a Published version is
  // viewed or superseded by a new version; a Retired one is only viewed. The server enforces the same rule.
  function actionsFor(record: Questionnaire): DataAction[] {
    const canCreate = permissions.can("Questionnaires", "create");
    const canEdit = permissions.can("Questionnaires", "edit");
    const canDelete = permissions.can("Questionnaires", "delete");
    switch (record?.status) {
      case "Draft":
        return [
          { key: "update", label: "Edit", disabled: !canEdit, handler: onAction },
          { key: "publish", label: "Publish", disabled: !canCreate, handler: onAction },
          { key: "delete", label: "Discard draft", danger: true, disabled: !canDelete, handler: onAction },
        ];
      case "Published":
        return [
          { key: "view", label: "View", handler: onAction },
          { key: "newVersion", label: "New version", disabled: !canCreate, handler: onAction },
        ];
      default:
        return [{ key: "view", label: "View", handler: onAction }];
    }
  }

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{
    readonly searchQuery: SearchQuery;
  }>({
    searchQuery: {
      conditions: [],
    },
  });
  const [valuesValid, setValuesValid] = useState(false);

  // Lifecycle
  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  useEffect(() => {
    setContext({ ...context });
  }, [props.trigger]);

  // Events
  function onLoading(loading: boolean): void {
    setLoading(loading);
  }

  function onSearchData(): void {
    const searchQuery = mapQuestionnairesViewQuery(values);
    setContext({
      searchQuery,
    });
    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({
      searchQuery: {
        conditions: [],
      },
    });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  // Methods
  const request = createGridRequest(getQuestionnaires, { retry: true });

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Search Form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button disabled={!valuesValid || loading} onClick={onClearData}>
            Clear
          </Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!valuesValid}
            onClick={onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className="w-72 overflow-y-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                  },
                  {
                    type: "combobox",
                    name: "type",
                    label: "Type",
                    request: () =>
                      Promise.resolve([
                        { display: "Test", value: "Test" },
                        { display: "Survey", value: "Survey" },
                        { display: "Roadmap", value: "Roadmap" },
                      ]),
                  },
                  {
                    type: "combobox",
                    name: "status",
                    label: "Status",
                    request: () =>
                      Promise.resolve(QUESTIONNAIRE_STATUSES.map((s) => ({ display: s, value: s }))),
                  },
                  {
                    type: "textbox",
                    name: "createdByUserUserName",
                    label: "Created by",
                  },
                  {
                    type: "rangetimepicker",
                    name: "createdOn",
                    label: "Created On",
                  },
                ],
              },
            ],
          }}
        />
      </Col>
      {/* Data Grid */}
      <Data
        columns={questionnairesViewColumns}
        context={context}
        pageSize={100}
        actions={actionsFor}
        request={request}
        rowKey={({ uid }) => uid}
        onLoading={onLoading}
      />
    </Row>
  );
}

export default QuestionnairesView;
